import { useState } from 'react';
import {
  Drawer,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Divider,
  Alert,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Search,
  X,
  PhoneCall,
  Mic,
  MessageSquare,
  Image,
  SearchX,
  DollarSign,
  Lock,
  Info,
} from 'lucide-react';
import { usePhoneNumbers } from '../../hooks/phone/usePhoneNumbers';
import {
  AvailablePhoneNumber,
  PhoneNumberType,
  SearchPhoneNumbersParams,
} from '../../types/phone-numbers';

const NUMBER_TYPES: { value: PhoneNumberType; label: string }[] = [
  { value: 'local', label: 'Local' },
  { value: 'tollFree', label: 'Toll-Free' },
  { value: 'mobile', label: 'Mobile' },
];

const PLATFORM_FEE = 2.0;

const SUPPORTED_COUNTRIES = [
  { code: 'AU', label: 'Australia (+61)' },
  { code: 'US', label: 'United States (+1)' },
  { code: 'GB', label: 'United Kingdom (+44)' },
  { code: 'NZ', label: 'New Zealand (+64)' },
  { code: 'CA', label: 'Canada (+1)' },
];

interface SearchPhoneDialogProps {
  open: boolean;
  onClose: () => void;
  onPurchased: () => void;
  subaccountSid: string;
}

const SearchPhoneDialog = ({
  open,
  onClose,
  onPurchased,
  subaccountSid,
}: SearchPhoneDialogProps) => {
  const theme = useTheme();
  const { availableNumbers, isSearching, isPurchasing, searchAvailable, purchase, clearSearch } =
    usePhoneNumbers();

  const [country, setCountry] = useState('AU');
  const [numberType, setNumberType] = useState<PhoneNumberType>('local');
  const [contains, setContains] = useState('');
  const [confirmNumber, setConfirmNumber] = useState<AvailablePhoneNumber | null>(null);
  const [friendlyName, setFriendlyName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchedType, setSearchedType] = useState<PhoneNumberType>('local');

  const handleSearch = async () => {
    setError(null);
    const params: SearchPhoneNumbersParams = { country, numberType, limit: 20 };
    if (contains.trim()) params.contains = contains.trim();
    try {
      await searchAvailable(params);
      setSearchedType(numberType);
      setHasSearched(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed. Please try again.');
    }
  };

  const handleBuyClick = (number: AvailablePhoneNumber) => {
    setConfirmNumber(number);
    setFriendlyName('');
    setError(null);
  };

  const handleConfirmPurchase = async () => {
    if (!confirmNumber) return;

    if (!subaccountSid) {
      setError('Twilio subaccount not configured. Please activate your phone system first.');
      return;
    }

    setError(null);
    try {
      await purchase({
        phoneNumber: confirmNumber.phoneNumber,
        subaccountSid,
        ...(friendlyName.trim() ? { friendlyName: friendlyName.trim() } : {}),
      });
      setPurchaseSuccess(true);
      onPurchased();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Purchase failed. Please try again.';
      // Clean up nested "Error: " prefixes from backend
      setError(msg.replace(/^(Error:\s*)+/i, ''));
    }
  };

  const handleClose = () => {
    setConfirmNumber(null);
    setFriendlyName('');
    setError(null);
    setPurchaseSuccess(false);
    setContains('');
    setHasSearched(false);
    clearSearch();
    onClose();
  };

  const handleBackToSearch = () => {
    setConfirmNumber(null);
    setFriendlyName('');
    setError(null);
    setPurchaseSuccess(false);
  };

  const drawerHeader = (title: string) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 3,
        py: 2,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography variant="h6" fontWeight={500}>
        {title}
      </Typography>
      <IconButton size="small" onClick={handleClose}>
        <X size={18} />
      </IconButton>
    </Box>
  );

  const renderContent = () => {
    if (purchaseSuccess && confirmNumber) {
      return (
        <>
          {drawerHeader('Number Purchased')}
          <Box sx={{ p: 3 }}>
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <PhoneCall size={28} color={theme.palette.success.main} />
              </Box>
              <Typography variant="h6" gutterBottom>
                {confirmNumber.phoneNumber}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your number has been purchased successfully. It is pending KYC verification.
              </Typography>
            </Box>
            <Button variant="contained" disableElevation onClick={handleClose} fullWidth>
              Done
            </Button>
          </Box>
        </>
      );
    }

    if (confirmNumber) {
      return (
        <>
          {drawerHeader('Confirm Purchase')}
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                p: 2,
                mb: 2,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.primary.main, 0.04),
              }}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                {confirmNumber.phoneNumber}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
                {confirmNumber.capabilities.voice && (
                  <Chip
                    icon={<Mic size={12} />}
                    label="Voice"
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {confirmNumber.capabilities.sms && (
                  <Chip
                    icon={<MessageSquare size={12} />}
                    label="SMS"
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                )}
                {confirmNumber.capabilities.mms && (
                  <Chip
                    icon={<Image size={12} />}
                    label="MMS"
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>

            <TextField
              label="Friendly Name (optional)"
              value={friendlyName}
              onChange={e => setFriendlyName(e.target.value)}
              fullWidth
              size="small"
              placeholder="e.g. Reception, Sales"
              helperText="A human-readable label for this number"
            />

            <Box
              sx={{
                mt: 2,
                p: 2,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <DollarSign size={16} color={theme.palette.text.secondary} />
                <Typography variant="subtitle2">Pricing</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Number cost
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ${(confirmNumber.monthlyPrice ?? 0).toFixed(2)}/mo
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Platform fee
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ${PLATFORM_FEE.toFixed(2)}/mo
                </Typography>
              </Box>
              <Divider sx={{ mb: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2">Total</Typography>
                <Typography variant="subtitle2">
                  ${((confirmNumber.monthlyPrice ?? 0) + PLATFORM_FEE).toFixed(2)}/mo
                </Typography>
              </Box>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
              <Button variant="outlined" onClick={handleBackToSearch} disabled={isPurchasing}>
                Back
              </Button>
              <Button
                variant="contained"
                disableElevation
                onClick={handleConfirmPurchase}
                disabled={isPurchasing}
                startIcon={
                  isPurchasing ? <CircularProgress size={16} color="inherit" /> : undefined
                }
                sx={{ flex: 1 }}
              >
                {isPurchasing ? 'Purchasing...' : 'Confirm Purchase'}
              </Button>
            </Box>
          </Box>
        </>
      );
    }

    return (
      <>
        {drawerHeader('Search Available Numbers')}
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
            <TextField
              select
              label="Country"
              value={country}
              onChange={e => setCountry(e.target.value)}
              size="small"
              sx={{ minWidth: 180 }}
            >
              {SUPPORTED_COUNTRIES.map(c => (
                <MenuItem key={c.code} value={c.code}>
                  {c.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Contains"
              value={contains}
              onChange={e => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 3);
                setContains(val);
              }}
              size="small"
              placeholder="e.g. 123"
              helperText="Up to 3 digits"
              sx={{ flex: 1 }}
              inputProps={{ maxLength: 3 }}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <TextField
              select
              label="Type"
              value={numberType}
              onChange={e => setNumberType(e.target.value as PhoneNumberType)}
              size="small"
              sx={{ minWidth: 140 }}
            >
              {NUMBER_TYPES.map(t => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              disableElevation
              startIcon={
                isSearching ? <CircularProgress size={16} color="inherit" /> : <Search size={16} />
              }
              onClick={handleSearch}
              disabled={isSearching}
            >
              {isSearching ? 'Searching...' : 'Search'}
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {availableNumbers.length > 0 && searchedType !== 'local' && (
            <Alert severity="info" icon={<Info size={18} />} sx={{ mb: 1.5 }}>
              To register a {searchedType === 'tollFree' ? 'Toll-Free' : 'Mobile'} number, please
              email{' '}
              <Typography
                component="a"
                href="mailto:contact@cacxi.com"
                variant="body2"
                fontWeight={600}
                sx={{ color: 'inherit' }}
              >
                contact@cacxi.com
              </Typography>
            </Alert>
          )}

          {availableNumbers.length > 0 && (
            <>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                {availableNumbers.length} number{availableNumbers.length !== 1 ? 's' : ''} available
              </Typography>
              <Box sx={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
                {availableNumbers.map(number => (
                  <Box
                    key={number.phoneNumber}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 1.25,
                      px: 1.5,
                      borderRadius: 1,
                      mb: 0.5,
                      border: `1px solid ${theme.palette.divider}`,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.action.hover, 0.5),
                      },
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {number.phoneNumber}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                        {number.capabilities.voice && (
                          <Chip
                            label="Voice"
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                        {number.capabilities.sms && (
                          <Chip
                            label="SMS"
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                        {number.capabilities.mms && (
                          <Chip
                            label="MMS"
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                      </Box>
                    </Box>
                    {searchedType === 'local' ? (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleBuyClick(number)}
                        disableElevation
                      >
                        Buy ${((number.monthlyPrice ?? 0) + PLATFORM_FEE).toFixed(2)}/mo
                      </Button>
                    ) : (
                      <Tooltip
                        title="To register a Toll-Free or Mobile number, please email contact@cacxi.com"
                        arrow
                        placement="left"
                      >
                        <span>
                          <Button
                            variant="outlined"
                            size="small"
                            disabled
                            startIcon={<Lock size={14} />}
                            disableElevation
                          >
                            Buy ${((number.monthlyPrice ?? 0) + PLATFORM_FEE).toFixed(2)}/mo
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                ))}
              </Box>
            </>
          )}

          {!isSearching && hasSearched && availableNumbers.length === 0 && (
            <Box
              sx={{
                textAlign: 'center',
                py: 4,
                color: 'text.secondary',
              }}
            >
              <SearchX size={32} color={theme.palette.text.disabled} />
              <Typography variant="body2" fontWeight={500} sx={{ mt: 1 }}>
                No numbers found
              </Typography>
              <Typography variant="caption" color="text.disabled">
                Try a different country or area code
              </Typography>
            </Box>
          )}

          {!isSearching && !hasSearched && (
            <Box
              sx={{
                textAlign: 'center',
                py: 4,
                color: 'text.secondary',
              }}
            >
              <Search size={32} color={theme.palette.text.disabled} />
              <Typography variant="body2" sx={{ mt: 1 }}>
                Select a country and click Search to find available numbers
              </Typography>
            </Box>
          )}
        </Box>
      </>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: '50%' } },
      }}
    >
      {renderContent()}
    </Drawer>
  );
};

export default SearchPhoneDialog;
